import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import client from '../../api/client'
import { useAuthStore } from '../../store/authStore'
import Spinner from '../../components/Spinner'
import { toast } from '../../components/Toast'

type FoodItem = { name: string; icon: string; reason: string }

type FoodData = {
  date: string
  season: string
  day_gan: string
  day_ji: string
  today_element: string
  theme: string
  taste: string
  condition: 'excess' | 'deficient' | 'balanced'
  condition_msg: string
  recommend_element: string
  recommend: FoodItem[]
  avoid: string[]
  weak_element: string
  weak_bonus: { element: string; food: FoodItem; message: string }
}

const ELEMENT_STYLE: Record<string, { bg: string; border: string; text: string; badge: string }> = {
  목: { bg: 'bg-green-900/20', border: 'border-green-500/30', text: 'text-green-400', badge: 'bg-green-900/30 text-green-400' },
  화: { bg: 'bg-red-900/20',   border: 'border-red-500/30',   text: 'text-red-400',   badge: 'bg-red-900/30 text-red-400' },
  토: { bg: 'bg-yellow-900/20', border: 'border-yellow-500/30', text: 'text-yellow-400', badge: 'bg-yellow-900/30 text-yellow-400' },
  금: { bg: 'bg-slate-700/20', border: 'border-slate-500/30', text: 'text-slate-300',  badge: 'bg-slate-700/30 text-slate-300' },
  수: { bg: 'bg-blue-900/20',  border: 'border-blue-500/30',  text: 'text-blue-400',  badge: 'bg-blue-900/30 text-blue-400' },
}

const ELEMENT_EMOJI: Record<string, string> = {
  목: '🌿', 화: '🔥', 토: '🏔️', 금: '⚙️', 수: '💧',
}

export default function FoodToday() {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuthStore()
  const [data, setData] = useState<FoodData | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!isAuthenticated) { navigate('/login'); return }
    client.get('/features/food-today')
      .then((r) => setData(r.data))
      .catch(() => toast('음식 추천을 불러오지 못했습니다.', 'error'))
      .finally(() => setLoading(false))
  }, [isAuthenticated])

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--color-bg)] flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    )
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[var(--color-bg)] flex items-center justify-center">
        <p className="text-[var(--color-text-tertiary)]">데이터를 불러올 수 없습니다.</p>
      </div>
    )
  }

  const style = ELEMENT_STYLE[data.today_element] ?? ELEMENT_STYLE['토']
  const recommendStyle = ELEMENT_STYLE[data.recommend_element] ?? ELEMENT_STYLE['토']
  const weakStyle = ELEMENT_STYLE[data.weak_element] ?? ELEMENT_STYLE['토']

  const CONDITION_LABEL: Record<string, string> = {
    excess: '⚖️ 과다 → 균형 추천',
    deficient: '🌱 부족 → 보충 추천',
    balanced: '✨ 오늘의 추천',
  }

  return (
    <div className="min-h-screen bg-[var(--color-bg)] p-4 sm:p-6">
      <div className="max-w-lg mx-auto space-y-4">

        {/* 헤더 */}
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/dashboard')} className="text-[var(--color-text-tertiary)] hover:text-[var(--color-text-primary)] p-1">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-xl font-bold text-[var(--color-text-primary)]">🍽️ 오늘 뭐 먹지?</h1>
        </div>

        {/* 오늘의 일진 + 테마 */}
        <div className={`${style.bg} border ${style.border} rounded-2xl p-5`}>
          <div className="flex items-center gap-3 mb-3">
            <span className="text-4xl">{ELEMENT_EMOJI[data.today_element]}</span>
            <div>
              <p className="text-[var(--color-text-tertiary)] text-xs">오늘의 일진 · {data.season}</p>
              <p className={`font-bold text-lg ${style.text}`}>
                {data.day_gan}{data.day_ji}일 · {data.today_element} 기운
              </p>
            </div>
          </div>
          <p className="text-[var(--color-text-secondary)] text-sm leading-relaxed">{data.theme}</p>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <span className={`text-xs px-2.5 py-1 rounded-full ${style.badge}`}>
              추천 맛: {data.taste}
            </span>
            <span className="text-[var(--color-text-disabled)] text-[10px]">오행 균형 기반 · 행운 음식과 별개</span>
          </div>
        </div>

        {/* 오늘의 추천 음식 */}
        <div className="bg-[var(--color-surface)] border border-[var(--color-border-default)] rounded-2xl p-5">
          <div className="flex items-center justify-between mb-1">
            <p className="text-[var(--color-accent)] text-sm font-bold">{CONDITION_LABEL[data.condition]} {ELEMENT_EMOJI[data.recommend_element]}</p>
            {data.recommend_element !== data.today_element && (
              <span className={`text-xs px-2 py-0.5 rounded-full ${recommendStyle.badge}`}>
                {data.recommend_element} 기운
              </span>
            )}
          </div>
          <p className="text-[var(--color-text-tertiary)] text-xs mb-4">{data.condition_msg}</p>
          <div className="space-y-3">
            {data.recommend.map((food, i) => (
              <div key={food.name} className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center text-xl flex-shrink-0">
                  {food.icon}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-[var(--color-text-primary)] text-sm font-medium">{food.name}</p>
                    {i === 0 && (
                      <span className="text-xs bg-[var(--color-accent)]/20 text-[var(--color-accent)] px-1.5 py-0.5 rounded-full">추천</span>
                    )}
                  </div>
                  <p className="text-[var(--color-text-tertiary)] text-xs mt-0.5">{food.reason}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 내 부족한 오행 보너스 추천 */}
        <div className={`${weakStyle.bg} border ${weakStyle.border} rounded-2xl p-4`}>
          <p className={`text-xs font-bold mb-2 ${weakStyle.text}`}>
            {ELEMENT_EMOJI[data.weak_element]} 나의 {data.weak_element} 기운 보충
          </p>
          <div className="flex items-center gap-3">
            <span className="text-2xl">{data.weak_bonus.food.icon}</span>
            <div>
              <p className="text-[var(--color-text-primary)] text-sm font-medium">{data.weak_bonus.food.name}</p>
              <p className="text-[var(--color-text-tertiary)] text-xs mt-0.5">{data.weak_bonus.message}</p>
            </div>
          </div>
        </div>

        {/* 피할 음식 */}
        <div className="bg-[var(--color-surface)] border border-[var(--color-border-default)] rounded-2xl p-4">
          <p className="text-[var(--color-text-tertiary)] text-xs font-bold mb-2">⚠️ 오늘 피하면 좋은 것</p>
          <div className="flex gap-2 flex-wrap">
            {data.avoid.map((item) => (
              <span key={item} className="text-xs bg-red-900/20 border border-red-500/30 text-red-400 px-2.5 py-1 rounded-full">
                {item}
              </span>
            ))}
          </div>
        </div>

        {/* AI 상담 연결 */}
        <button
          onClick={() => navigate('/counseling', { state: { autoStart: true, category: 'health', mode: 'chat' } })}
          className="w-full py-3 bg-[var(--color-accent)] hover:opacity-90 text-[var(--color-primary)] rounded-xl font-bold text-sm transition-opacity"
        >
          💬 식습관·건강 AI 상담 받기
        </button>

      </div>
    </div>
  )
}
